'use client';

import { Icon, Marker, PointExpression } from 'leaflet';
import React, { Ref, useMemo, useState } from 'react';
import { FunctionComponent } from 'react';
import { Marker as MarkerComponent, MarkerProps } from 'react-leaflet';
import './map-marker.css';
import classNames from 'classnames';
import { Point } from 'geojson';

export type MapMarkerRef = Marker;

type MapMarkerProps = {
  type?: 'marker' | 'location' | 'image-circle';
  url?: string;
  position: Point;
  hoverable?: boolean;
  active?: boolean;
  forwardedRef?: Ref<MapMarkerRef>;
} & Omit<MarkerProps, 'position'>;

const MapMarker: FunctionComponent<MapMarkerProps> = ({
  type = 'marker',
  position,
  eventHandlers,
  hoverable = false,
  forwardedRef,
  active = false,
  url,
  ...rest
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const isActive = useMemo(
    () => (hoverable && isHovered) || active,
    [active, hoverable, isHovered],
  );

  const iconSize = useMemo<[number, number]>(() => {
    if (type === 'image-circle') return isActive ? [50, 50] : [40, 40];
    return isActive ? [50, 50] : [30, 30];
  }, [type, isActive]);

  const iconUrl = useMemo(() => {
    if (type !== 'image-circle') {
      return isActive && type === 'marker'
        ? `/images/map-active.svg`
        : `/images/map-${type}.svg`;
    }

    if (!url) throw new Error('url is required for image-circle type');

    return url;
  }, [isActive]);

  return (
    <MarkerComponent
      ref={forwardedRef}
      eventHandlers={{
        ...eventHandlers,
        mouseout: (event) => {
          setIsHovered(false);
          eventHandlers?.mouseout?.(event);
        },
        mouseover: (event) => {
          setIsHovered(true);
          eventHandlers?.mouseover?.(event);
        },
      }}
      icon={
        new Icon({
          iconUrl: iconUrl,
          iconSize: iconSize as PointExpression,
          iconAnchor:
            type !== 'image-circle'
              ? [iconSize[0] / 2, iconSize[0]]
              : undefined,
          className: classNames('marker', {
            imageCircle: type === 'image-circle',
            active: isActive,
          }),
        })
      }
      zIndexOffset={isActive ? 2100 : 0}
      position={{
        lng: position.coordinates[0],
        lat: position.coordinates[1],
      }}
      {...rest}
    />
  );
};

export default MapMarker;
